import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import { type RootState } from '../../../../services/serviceTypes';
import {
  addVisitedStep,
  closeSaveDraftModal,
  revertStepFormData,
  setActiveStep,
  setCurrentEntitlementIndex,
  setFormStatus,
  setRemoveAccessEntitlementIndex,
  updateStepFormData,
} from '../../data-store/copilotSlice';
import showStepForm from '../../utils/stepFormDisplayUtils';

import './SaveDraftModalIsland.css';

/**
 * SaveDraftModalIsland component for handling unsaved changes
 */
function SaveDraftModalIsland({
  scrollToStep,
}: {
  scrollToStep: (stepId: string) => void;
}) {
  const dispatch = useDispatch();

  const { isOpen, stepName, targetStep, currentMode } = useSelector(
    (state: RootState) => ({
      isOpen: state.copilot.saveDraftModal.isOpen,
      stepName: state.copilot.saveDraftModal.stepName,
      targetStep: state.copilot.saveDraftModal.targetStep,
      currentMode: state.copilot.ui.mode,
    })
  );

  const allSavedStepData = useSelector(
    (state: RootState) => state.copilot.savedStepData
  );

  const allStepFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData
  );

  // useEffect to ensure proper form status when modal is involved
  useEffect(() => {
    if (isOpen && stepName) {
      // When modal opens, ensure the step with changes is in expandable view
      dispatch(setFormStatus({ stepName, formStatus: 'expandableview' }));
    }
  }, [isOpen, stepName, dispatch]);

  // useEffect to handle form status after modal closes
  useEffect(() => {
    if (!isOpen && stepName) {
      // Small delay to ensure modal close animation completes
      const timer = setTimeout(() => {
        // Ensure proper form status based on the action taken
        dispatch(setFormStatus({ stepName, formStatus: 'expandableview' }));
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isOpen, stepName, dispatch]);

  const handleSave = () => {
    // Get form container
    const formContainer = document.querySelector(`[data-step="${stepName}"]`);

    if (formContainer) {
      // Find the save button (Button-filled) in the footer, not skip button
      const saveButton = formContainer.querySelector(
        '.Integrations-ExpandableForm-Footer button.Button-filled'
      );

      if (
        saveButton &&
        saveButton instanceof HTMLButtonElement &&
        !saveButton.disabled
      ) {
        // Click the save button to trigger form submission
        saveButton.click();
      }
    }

    // Close modal immediately - user will see validation errors or success
    dispatch(closeSaveDraftModal());
  };

  const handleDiscard = () => {
    if (stepName.includes('Access')) {
      // Handle access steps - revert specific entitlement step
      const revertedData = allSavedStepData[stepName];

      if (revertedData) {
        dispatch(revertStepFormData(stepName));
        dispatch(
          updateStepFormData({
            stepName,
            formData: revertedData,
          })
        );
      } else {
        // If no saved data, just revert to empty
        dispatch(revertStepFormData(stepName));
      }

      // Navigate to target step (same as regular steps)
      try {
        scrollToStep(targetStep);
      } catch (error) {
        // Ignore scroll errors for entitlement steps
        Logger.log('Scroll error ignored:', error);
      }

      // Set correct entitlement index for target step
      if (targetStep.startsWith('AddAccess_')) {
        const index = parseInt(targetStep.split('_')[1], 10);

        if (!Number.isNaN(index)) {
          dispatch(setCurrentEntitlementIndex(index));
        }
      } else if (targetStep.includes('RemoveAccess_')) {
        const index = parseInt(targetStep.split('_')[1], 10);

        if (!Number.isNaN(index)) {
          dispatch(setRemoveAccessEntitlementIndex(index));
        }
      }

      dispatch(setActiveStep(targetStep));
      dispatch(
        setFormStatus({ stepName: targetStep, formStatus: 'expandableview' })
      );
      dispatch(addVisitedStep(targetStep));

      dispatch(closeSaveDraftModal());

      return;
    }

    // Regular steps - revert stepFormData back to savedStepData
    dispatch(revertStepFormData(stepName));

    // Navigate to target step
    try {
      scrollToStep(targetStep);
    } catch (error) {
      // Ignore scroll errors for entitlement steps
      Logger.warn('Scroll error ignored:', error);
    }

    // Set correct entitlement index for target step
    if (targetStep.startsWith('AddAccess_')) {
      const index = parseInt(targetStep.split('_')[1], 10);

      if (!Number.isNaN(index)) {
        dispatch(setCurrentEntitlementIndex(index));
      }
    } else if (targetStep.includes('RemoveAccess_')) {
      const index = parseInt(targetStep.split('_')[1], 10);

      if (!Number.isNaN(index)) {
        dispatch(setRemoveAccessEntitlementIndex(index));
      }
    }

    dispatch(setActiveStep(targetStep));
    dispatch(
      setFormStatus({ stepName: targetStep, formStatus: 'expandableview' })
    );
    dispatch(addVisitedStep(targetStep));

    // In edit mode, load fresh data for the target step
    if (currentMode === 'edit') {
      showStepForm(dispatch, allStepFormData, targetStep, true, () => ({
        copilot: { parsedTemplates: {} },
      }));
      dispatch(closeSaveDraftModal());

      return;
    }

    // Collapse the discarded step
    dispatch(setFormStatus({ stepName, formStatus: 'collapsedview' }));

    // Close modal
    dispatch(closeSaveDraftModal());
  };

  const handleCancel = () => {
    dispatch(closeSaveDraftModal());
  };

  const bodyContent = (
    <Typography kind='body2' className='Draft-ModalIsland-Body'>
      Some changes haven’t been saved. Are you sure you want to continue without
      saving?
    </Typography>
  );

  const footerActions = (
    <Box className='Draft-ModalIsland-FooterActionButtons'>
      <Button kind='outlined' size='medium' onClick={handleDiscard}>
        Discard Changes
      </Button>
      <Button kind='filled' size='medium' onClick={handleSave}>
        Save Changes
      </Button>
    </Box>
  );

  return (
    <ModalIsland
      onClose={handleCancel}
      isOpen={isOpen}
      title='Unsaved Changes'
      contentBody={bodyContent}
      isCloseButtonVisible={false}
      isCloseOnEscapeKeyEnabled={false}
      isOutsideClickEnabled={false}
      FooterActionBarComp={
        <FooterActionBar
          size='large'
          className='Draft-ModalIsland-FooterActions'>
          {footerActions}
        </FooterActionBar>
      }
    />
  );
}

export default SaveDraftModalIsland;
